import Seo from "../components/Seo";
import { Logo } from "../components/landing/Logo";
import LeadForm from "../components/landing/LeadForm";
import { ArrowRight, Zap, ShieldCheck, CalendarCheck } from "lucide-react";

const BULLETS = [
  {
    icon: Zap,
    title: "Answers in seconds, 24/7",
    body: "A company-branded text goes out the moment a call is missed — nights, weekends, and when your whole office is on other lines.",
  },
  {
    icon: ShieldCheck,
    title: "Qualifies and escalates",
    body: "Repair or replacement, emergency or routine. Real emergencies (gas, leaks, sparks) page your on-call phone immediately.",
  },
  {
    icon: CalendarCheck,
    title: "Books the job",
    body: "The customer confirms a window, the job lands in your dashboard, and your office takes it from there.",
  },
];

export default function LandingAd() {
  return (
    <div data-testid="landing-ad-page" className="min-h-screen bg-[#0B0D10] text-slate-100">
      <Seo
        title="Missed Call Text-Back for Contractors | Backfill"
        description="Backfill texts back every missed call in seconds, qualifies the job, escalates emergencies, and books the work — built for HVAC and home-service contractors."
        noindex
      />
      <header className="border-b border-white/[0.06]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <a href="/" aria-label="Backfill home"><Logo /></a>
          <a
            href="#demo"
            data-testid="landing-ad-header-cta"
            className="inline-flex items-center gap-2 rounded-md bg-[#F25F22] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#d94e15]"
          >
            Book a walkthrough <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-6xl px-5 pb-20 pt-16 sm:px-8 lg:pt-24">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#F25F22]">
                Missed-call text-back for contractors
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight text-slate-50 sm:text-5xl">
                Every missed call texts back in seconds.{" "}
                <span className="text-[#F25F22]">You do nothing.</span>
              </h1>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-400">
                Homeowners call the next contractor on the list when you don&apos;t pick up.
                Backfill catches the call, qualifies the job by text, and hands your office a
                booked appointment — not a voicemail.
              </p>
              <div className="mt-8 space-y-5">
                {BULLETS.map((b, i) => (
                  <div key={b.title} className="flex gap-4" data-testid={`landing-ad-bullet-${i}`}>
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#F25F22]/30 bg-[#F25F22]/10">
                      <b.icon className="h-4 w-4 text-[#F25F22]" />
                    </div>
                    <div>
                      <p className="font-display text-sm font-semibold text-slate-100">{b.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-400">{b.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="overflow-hidden rounded-2xl border border-white/[0.1] shadow-[0_40px_80px_-24px_rgba(0,0,0,0.8)]">
                <video
                  controls
                  autoPlay
                  muted
                  playsInline
                  preload="metadata"
                  data-testid="landing-ad-video"
                  className="w-full bg-black"
                >
                  <source src="/backfill-demo.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                20-second narrated demo — sound on
              </p>
            </div>
          </div>
        </section>

        <LeadForm source="google-ads-missed-call" />
      </main>

      <footer className="border-t border-white/[0.06] py-8 text-center">
        <p className="text-xs text-slate-600">
          © {new Date().getFullYear()} Backfill ·{" "}
          <a href="/" className="underline underline-offset-2 hover:text-slate-400">Full site</a>
        </p>
      </footer>
    </div>
  );
}
